import React, { useState, useEffect, useMemo } from 'react';
import './ProductionDashboard.css';

/**
 * Production wall board (the Fire TV). Cycles through three pages:
 *   1. builds   - NetSuite assembly builds by day, weekly goal
 *   2. stations - what Carting / Wicking / Pouring did today and this week
 *   3. leaders  - fastest wickers and carters by seconds per unit
 *
 * Query params for testing on a laptop: ?page=stations pins a page, ?rotate=10 sets
 * the cycle in seconds. Nothing on this page is interactive: the TV has no remote.
 */

const PAGES = ['builds', 'stations', 'leaders'];
const DEFAULT_ROTATE_S = 20;
const REFRESH_MS = 60000;
const WEEKLY_GOAL = 6000;
const ACTIVE_WINDOW_MS = 20 * 60 * 1000;

const formatNumber = (num) => (num || 0).toLocaleString();

// Day of month from YYYY-MM-DD, parsed at noon so the zone can't shift the day.
const getDayNum = (dateStr) => new Date(dateStr + 'T12:00:00').getDate();

const timeAgo = (iso, now) => {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const secLabel = (s) => (s === null || s === undefined ? '-' : `${s}s`);

function BuildsPage({ builds }) {
  if (!builds) return <EmptyPage />;
  return (
    <div className="netsuite-builds-section">
      <div className="builds-summary-cards">
        <div className="builds-summary-card">
          <div className="builds-summary-value">{formatNumber(builds.summary.avgPerWeek)}</div>
          <div className="builds-summary-label">Avg/Week</div>
        </div>
        <div className="builds-summary-card">
          <div className="builds-summary-value">{formatNumber(builds.summary.avgPerMonth)}</div>
          <div className="builds-summary-label">Avg/Month</div>
        </div>
        <div className="builds-summary-card">
          <div className="builds-summary-value">{formatNumber(builds.summary.projectedAnnual)}</div>
          <div className="builds-summary-label">Projected Annual</div>
        </div>
      </div>

      <div className="builds-weekly-grid">
        <table className="builds-table">
          <thead>
            <tr>
              <th className="week-label-col"></th>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th className="week-total-col">Total</th>
            </tr>
          </thead>
          <tbody>
            {builds.weeks.map((week, idx) => {
              const isCurrentWeek = idx === 0;
              const meetsGoal = week.total >= WEEKLY_GOAL;
              return (
                <tr key={week.weekStart}>
                  <td className="week-label">{week.weekLabel}</td>
                  {week.days.map(day => (
                    <td key={day.date} className={`day-cell ${day.units === null ? 'future' : day.units === 0 ? 'zero' : ''}`}>
                      <span className="day-date">{getDayNum(day.date)}</span>
                      <span className="day-units">{day.units === null ? '-' : formatNumber(day.units)}</span>
                    </td>
                  ))}
                  <td className={`week-total ${!isCurrentWeek ? (meetsGoal ? 'goal-met' : 'goal-missed') : ''}`}>
                    {formatNumber(week.total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StationCard({ station, now }) {
  const { label, today, week, lastScan, timed } = station;
  const lastAt = lastScan ? new Date(lastScan.at).getTime() : 0;
  const active = lastAt && now - lastAt < ACTIVE_WINDOW_MS;
  const quiet = today.carts === 0;
  const operators = timed ? today.operators.slice(0, 3) : [];

  return (
    <div className={`station-card ${active ? 'active' : quiet ? 'quiet' : ''}`}>
      <div className="station-head">
        <span className="station-name">{label}</span>
        <span className="station-status">
          <span className="status-dot" />
          {active ? 'Active' : quiet ? 'No scans today' : 'Idle'}
        </span>
      </div>

      <div className="station-big">
        <div className="station-big-value">{formatNumber(today.units)}</div>
        <div className="station-big-label">units today</div>
      </div>

      <div className="station-row">
        <div className="station-stat">
          <div className="station-stat-value">{today.carts}</div>
          <div className="station-stat-label">carts</div>
        </div>
        <div className="station-stat">
          <div className="station-stat-value">{timed ? secLabel(today.secPerUnit) : '-'}</div>
          <div className="station-stat-label">per unit</div>
        </div>
        <div className="station-stat">
          <div className="station-stat-value">{timed && today.unitsPerHour ? formatNumber(today.unitsPerHour) : '-'}</div>
          <div className="station-stat-label">units / hr</div>
        </div>
      </div>

      <div className="station-ops">
        {!timed && <span className="station-ops-none">Scanner station, no operator login</span>}
        {timed && operators.length === 0 && <span className="station-ops-none">Nobody scanned in yet</span>}
        {operators.map(op => (
          <span key={op.name} className="station-op">
            {op.name} <span className="station-op-units">{formatNumber(op.units)}</span>
          </span>
        ))}
      </div>

      <div className="station-foot">
        <div className="station-week">
          <span className="station-week-label">7 days</span>
          <span>{formatNumber(week.units)} units</span>
          <span>{week.carts} carts</span>
          {timed && week.secPerUnit !== null && <span>{secLabel(week.secPerUnit)} / unit</span>}
        </div>
        <div className="station-last">
          {lastScan ? (
            <>
              <span className="station-last-when">{timeAgo(lastScan.at, now)}</span>
              {timed && lastScan.by && <span> {lastScan.by}</span>}
              {lastScan.vessel && <span className="station-last-vessel"> {lastScan.vessel}</span>}
            </>
          ) : (
            <span className="station-last-when">No scans in 30 days</span>
          )}
        </div>
      </div>
    </div>
  );
}

function StationsPage({ stats, now }) {
  if (!stats) return <EmptyPage />;
  return (
    <div className="board-page stations-page">
      <div className="board-title">
        <span>Stations</span>
        <span className="board-subtitle">Today, Eastern time</span>
      </div>
      <div className="station-grid">
        {stats.stations.map(st => <StationCard key={st.key} station={st} now={now} />)}
      </div>
    </div>
  );
}

// Change vs the previous 30 days. Up arrow = faster (fewer seconds per unit).
const ARROWS = { faster: '\u25B2', slower: '\u25BC', flat: '\u2192' };

function Trend({ row }) {
  if (row.changePct === null || row.changePct === undefined) {
    return (
      <div className="leader-trend none">
        <div className="leader-trend-value">new</div>
        <div className="leader-trend-label">no prior 30d</div>
      </div>
    );
  }
  const word = row.trend === 'faster' ? 'faster' : row.trend === 'slower' ? 'slower' : 'same';
  return (
    <div className={`leader-trend ${row.trend}`}>
      <div className="leader-trend-value">{ARROWS[row.trend]} {Math.abs(row.changePct)}%</div>
      <div className="leader-trend-label">{word} vs prior 30d</div>
    </div>
  );
}

function LeaderList({ title, rows, emptyText }) {
  return (
    <div className="leader-col">
      <div className="leader-title">{title}</div>
      {rows.length === 0 && <div className="leader-empty">{emptyText}</div>}
      {rows.map((r, i) => (
        <div key={r.name} className={`leader-row ${i === 0 ? 'first' : ''}`}>
          <div className="leader-rank">{i + 1}</div>
          <div className="leader-name">
            {r.name}
            <div className="leader-sub">
              {formatNumber(r.units)} units in {r.carts} carts
              {r.todayUnits > 0 && <span> &middot; {formatNumber(r.todayUnits)} today</span>}
            </div>
          </div>
          <Trend row={r} />
          <div className="leader-speed">
            <div className="leader-speed-value">{r.secPerUnit}<span className="leader-speed-unit">s</span></div>
            <div className="leader-speed-label">per unit</div>
          </div>
          <div className="leader-rate">
            <div className="leader-rate-value">{formatNumber(r.unitsPerHour)}</div>
            <div className="leader-rate-label">units / hr</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadersPage({ stats }) {
  if (!stats) return <EmptyPage />;
  const { rules } = stats;
  return (
    <div className="board-page leaders-page">
      <div className="board-title">
        <span>Fastest per unit</span>
        <span className="board-subtitle">Last {rules.windowDays} days</span>
      </div>
      <div className="leader-grid">
        <LeaderList title="Top Wickers" rows={stats.leaders.wicking} emptyText="No timed wicking carts yet" />
        <LeaderList title="Top Carters" rows={stats.leaders.carting} emptyText="No timed carting carts yet" />
      </div>
      <div className="board-footnote">
        Median seconds per unit across each person&apos;s carts, fastest first. The arrow compares to that
        person&apos;s previous {rules.windowDays} days. Needs at least {rules.minCarts} carts.
        Carts under {rules.minElapsedMinutes} minute or outside {rules.minSecPerUnit}-{rules.maxSecPerUnit}s per unit are not counted.
      </div>
    </div>
  );
}

// Blank card while data is on its way. No spinner, no text: a TV across the room
// should never show a loading screen, just the last thing it had or nothing yet.
function EmptyPage() {
  return <div className="board-page board-empty" />;
}

function ProductionDashboard() {
  const [netsuiteBuilds, setNetsuiteBuilds] = useState(null);
  const [boardStats, setBoardStats] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [now, setNow] = useState(Date.now());

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const pinned = params.get('page');
  const rotateMs = Math.max(5, parseInt(params.get('rotate'), 10) || DEFAULT_ROTATE_S) * 1000;

  const fetchAll = async () => {
    const [builds, stats] = await Promise.all([
      fetch('/api/production/netsuite-builds').then(r => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/production/board-stats').then(r => (r.ok ? r.json() : null)).catch(() => null)
    ]);
    if (builds) setNetsuiteBuilds(builds);
    if (stats) setBoardStats(stats);
    setNow(Date.now());
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (pinned) return undefined;
    const t = setInterval(() => setPageIdx(i => (i + 1) % PAGES.length), rotateMs);
    return () => clearInterval(t);
  }, [pinned, rotateMs]);

  const page = pinned && PAGES.includes(pinned) ? pinned : PAGES[pageIdx];

  return (
    <div className="production-dashboard builds-only board">
      {page === 'builds' && <BuildsPage builds={netsuiteBuilds} />}
      {page === 'stations' && <StationsPage stats={boardStats} now={now} />}
      {page === 'leaders' && <LeadersPage stats={boardStats} />}

      <div className="board-dots">
        {PAGES.map(p => <span key={p} className={`board-dot ${p === page ? 'on' : ''}`} />)}
      </div>
    </div>
  );
}

export default ProductionDashboard;
